import React from 'react'

import { connect } from 'react-redux'
import { useIntercom } from 'react-use-intercom'

import { useCommunicationMode } from '@hooks'

import { SUPPORT_LAUNCHER_CLEARANCE } from '@components/Support/constants'

import { COMMUNICATION_MODE_INTERCOM } from '@constants'

import { INTERCOM_LAUNCHER_CLASS_NAME } from './constant'
import { getFullName, getIntercomAppId, getIntercomCompanyType } from './helpers'

interface IntercomLoaderProps {
  company: Nullable<Company>
  user: User
}

/** Hooks cannot run behind the app id guard, so the boot lives in a child that always renders. */
function IntercomLoader({ company, user }: IntercomLoaderProps) {
  const { boot, shutdown } = useIntercom()
  const communicationMode = useCommunicationMode()

  // Only intercom-mode companies render the navbar launcher, so only they have an element for
  // Intercom to bind to. A hubspot-mode company boots without one: it receives messages, but has
  // nothing to open the messenger with.
  const isIntercomMode = communicationMode === COMMUNICATION_MODE_INTERCOM
  const customLauncherSelector = isIntercomMode ? `.${INTERCOM_LAUNCHER_CLASS_NAME}` : undefined
  const verticalPadding = isIntercomMode ? undefined : SUPPORT_LAUNCHER_CLEARANCE

  // the fields rather than the company itself, so a new object from the store is not a new session
  const companyId = company?.id
  const companyName = company?.name
  const companyType = company?.company_type

  React.useLayoutEffect(() => {
    boot({
      customLauncherSelector,
      verticalPadding,
      hideDefaultLauncher: true,
      email: user.email,
      name: getFullName(user.first_name, user.last_name),
      userId: String(user.id),
      userHash: user.intercom_user_hash,
      sessionDuration: 10 * 60 * 60 * 1000, // milliseconds - same as auth token expirtion: 10 hrs
      // a company the user has not chosen yet, or is in the middle of switching, sends none of this
      ...(companyId != null && {
        customAttributes: {
          user_active_company_id: String(companyId),
        },
        company: {
          companyId: String(companyId),
          name: companyName,
          customAttributes: {
            company_type: getIntercomCompanyType(companyType),
          },
        },
      }),
    })

    return () => {
      shutdown()
    }
  }, [
    boot,
    companyId,
    companyName,
    companyType,
    customLauncherSelector,
    shutdown,
    user.email,
    user.first_name,
    user.id,
    user.intercom_user_hash,
    user.last_name,
    verticalPadding,
  ])

  return null
}

function PureCompanyBasedIntercomInitializer({ company, user }: IntercomLoaderProps) {
  if (!getIntercomAppId()) {
    return null
  }

  return <IntercomLoader company={company} user={user} />
}

export const CompanyBasedIntercomInitializer = connect((state: Store) => ({
  company: state.auth.company.data,
  user: state.auth.user,
}))(PureCompanyBasedIntercomInitializer)

CompanyBasedIntercomInitializer.displayName = 'CompanyBasedIntercomInitializer'
